import React from "react";
import {
  withStyles,
  Theme,
  createStyles,
  makeStyles,
} from "@material-ui/core/styles";
import moment from "moment/moment";
import type { User } from "firebase/app";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableFooter from "@material-ui/core/TableFooter";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TablePagination from "@material-ui/core/TablePagination";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import AssessmentIcon from "@material-ui/icons/Assessment";
import AccountBoxIcon from "@material-ui/icons/AccountBox";
import Tooltip from "@material-ui/core/Tooltip";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import Button from "@material-ui/core/Button";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import DatePicker from "../components/DatePicker";
import Autocomplete from "@material-ui/lab/Autocomplete";
import AmilReport from "./AmilReport";
import AmilProfile from "./AmilProfile";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "../store";
import type { Amil } from "../store/api";
import {
  setSearch,
  setAgency,
  setDateRange,
  setKariahChoice,
  updateKariahChoices,
} from "../store/form";
import { updateAmilList } from "../store/amil";
import type { SelectionKariah } from "../store/api";
import { formatMoney } from "../utils/number";

const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.common.white,
    },
    body: {
      fontSize: 14,
    },
  })
)(TableCell);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      marginTop: theme.spacing(2),
      overflowX: "auto",
    },
    table: {
      minWidth: 700,
    },
    category: {
      marginTop: theme.spacing(1),
    },
    textField: {
      marginTop: theme.spacing(1),
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      width: 200,
    },
    badge: {
      marginLeft: theme.spacing(1),
    },
    badges: {
      position: "absolute",
      marginLeft: theme.spacing(1),
      marginTop: theme.spacing(2),
    },
  })
);

interface Props {
  user: User;
}

interface AmilState {
  amil: Amil;
  action: "report" | "profile";
}

function AmilList({ user }: Props) {
  const classes = useStyles();

  const dispatch = useDispatch();
  const state = useSelector((state: RootState) => {
    const { appState, formState, amilState } = state;

    const { profile } = appState;
    const {
      search,
      start_date,
      end_date,
      agency,
      kariah_choice,
      kariah_choices,
    } = formState;
    const { amil_list } = amilState;

    return {
      profile,
      search,
      start_date,
      end_date,
      agency,
      amil_list,
      kariah_choice,
      kariah_choices,
    };
  });

  const {
    profile,
    search,
    start_date,
    end_date,
    agency,
    amil_list,
    kariah_choice,
    kariah_choices,
  } = state;

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [selected, setAmil] = React.useState<AmilState | null>(null);

  let kariah = "";
  if (kariah_choice !== null) {
    kariah = kariah_choice;
  }

  const closeAmilView = () => {
    setAmil(null);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearch(event.currentTarget.value));
  };

  React.useEffect(() => {
    (async () => {
      try {
        const token = await user.getIdToken();
        dispatch(
          updateAmilList(token, { start_date, end_date, agency, kariah })
        );
        dispatch(updateKariahChoices(token, agency));
      } catch (e) {
        console.log(e);
      }
    })();
  }, [agency, dispatch, end_date, kariah, start_date, user]);

  const download = async () => {
    try {
      const token = await user.getIdToken();
      dispatch(
        updateAmilList(token, {
          start_date,
          end_date,
          agency,
          kariah,
          download: "1",
        })
      );
    } catch (e) {
      console.log(e);
    }
  };

  if (selected) {
    const { action, amil } = selected;
    switch (action) {
      case "report":
        return (
          <AmilReport user={user} amil_ic={amil.ic} onClose={closeAmilView} />
        );
      case "profile":
        return <AmilProfile amil={amil} onClose={closeAmilView} />;
    }
  }

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const selected = event.target.value as string;
    dispatch(setAgency(selected));
  };

  const onDatesChange = (
    startDate: moment.Moment | null,
    endDate: moment.Moment | null
  ) => {
    if (startDate !== null && endDate !== null) {
      dispatch(
        setDateRange({
          start_date: startDate.format("YYYY-MM-DD"),
          end_date: endDate.format("YYYY-MM-DD"),
        })
      );
    }
  };

  let recordLength = amil_list.length;
  let copy = amil_list;
  if (search) {
    const keyword = search.toLowerCase();

    copy = amil_list.filter((row: Amil) => {
      if (row.email.toLowerCase().indexOf(keyword) >= 0) {
        return row;
      }

      if (row.name.toLowerCase().indexOf(keyword) >= 0) {
        return row;
      }

      if (row.ic.toLowerCase().indexOf(keyword) >= 0) {
        return row;
      }

      return null;
    });
    recordLength = copy.length;
  }

  const rows = copy.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const body = rows.map((row, i) => (
    <TableRow key={i.toString()}>
      <StyledTableCell>{row.ic}</StyledTableCell>
      <StyledTableCell align="right">{row.name}</StyledTableCell>
      <StyledTableCell align="right">{row.body}</StyledTableCell>
      <StyledTableCell align="right">{row.total_collected}</StyledTableCell>
      <StyledTableCell align="right">
        RM {formatMoney(row.amount_collected)}
      </StyledTableCell>
      <StyledTableCell align="right">
        {row.ic !== "" && (
          <>
            <Tooltip title="Laporan Kutipan">
              <IconButton
                onClick={() => {
                  setAmil({ amil: row, action: "report" });
                }}
              >
                <AssessmentIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Maklumat Peribadi">
              <IconButton
                onClick={() => {
                  setAmil({ amil: row, action: "profile" });
                }}
              >
                <AccountBoxIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
      </StyledTableCell>
    </TableRow>
  ));

  const setKariah = (
    evt: React.ChangeEvent<{}>,
    kariah: SelectionKariah | null
  ) => {
    if (kariah) {
      dispatch(setKariahChoice(kariah.code));
    } else {
      dispatch(setKariahChoice(null));
    }
  };

  return (
    <>
      <Typography variant="h4">Senarai Amil</Typography>

      <div style={{ float: "left", marginRight: 20 }}>
        <DatePicker start={start_date} end={end_date} onClick={onDatesChange} />
      </div>

      <div style={{ float: "left", marginRight: 20, marginTop: -8 }}>
        <TextField
          label="Cari"
          className={classes.textField}
          placeholder="Cari"
          value={search}
          onChange={onSearchChange}
        />
      </div>

      <div style={{ float: "left", marginRight: 20 }}>
        <Autocomplete
          multiple={false}
          options={kariah_choices}
          getOptionLabel={(kariah: SelectionKariah) =>
            `${kariah.name} (${kariah.code})`
          }
          style={{ width: 300 }}
          onChange={setKariah}
          renderInput={(params) => <TextField {...params} label="Kariah" />}
        />
      </div>

      <FormControl style={{ marginRight: 20, minWidth: 120 }}>
        <InputLabel>Agensi</InputLabel>
        <Select value={agency} onChange={handleChange}>
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {profile &&
            profile.access.map((item, i) => (
              <MenuItem key={i.toString()} value={item}>
                {item}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      <Button
        variant="contained"
        color="primary"
        onClick={download}
        style={{ marginTop: 10 }}
      >
        <CloudDownloadIcon />
        <Typography variant="body1" style={{ marginLeft: 10 }}>
          Muat Turun Laporan
        </Typography>
      </Button>

      <Paper className={classes.root}>
        <Table
          className={classes.table}
          aria-label="customized table"
          size="small"
        >
          <TableHead>
            <TableRow></TableRow>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[10, 20, 50]}
                colSpan={6}
                count={recordLength}
                rowsPerPage={rowsPerPage}
                page={page}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
              />
            </TableRow>
            <TableRow>
              <StyledTableCell>No KP</StyledTableCell>
              <StyledTableCell align="right">Nama</StyledTableCell>
              <StyledTableCell align="right">Perbadanan</StyledTableCell>
              <StyledTableCell align="right">Kutipan</StyledTableCell>
              <StyledTableCell align="right">Jumlah Dikutip</StyledTableCell>
              <StyledTableCell align="right"></StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>{body}</TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[10, 20, 50]}
                colSpan={6}
                count={recordLength}
                rowsPerPage={rowsPerPage}
                page={page}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </Paper>
    </>
  );
}

export default AmilList;
